import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Gift } from "lucide-react";
import { LegalConfigCard } from "@/components/admin/LegalConfigCard";

const planColor = (p) => ({
  internal: "bg-violet-100 text-violet-700",
  enterprise: "bg-indigo-100 text-indigo-700",
  pro: "bg-blue-100 text-blue-700",
  starter: "bg-emerald-100 text-emerald-700",
  free: "bg-slate-100 text-slate-600",
}[p] || "bg-slate-100 text-slate-600");

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ plans: [], billing_statuses: [] });
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (user && !user.platform_admin) { navigate("/", { replace: true }); return; }
    api.get("/platform/workspaces").then((r) => { setRows(r.data.workspaces); setMeta({ plans: r.data.plans, billing_statuses: r.data.billing_statuses }); }).catch(() => toast.error("Could not load workspaces"));
  }, [user, navigate]);

  const patch = (cid, field, value) => setRows((rs) => rs.map((r) => (r.company_id === cid ? { ...r, [field]: value } : r)));

  const save = async (row) => {
    setSavingId(row.company_id);
    try {
      const { data } = await api.put(`/platform/workspaces/${row.company_id}/billing`, {
        plan: row.plan,
        billing_status: row.billing_status,
        complimentary: row.complimentary,
        billing_customer_id: row.billing_customer_id || "",
        trial_end_date: row.trial_end_date || "",
      });
      setRows((rs) => rs.map((r) => (r.company_id === row.company_id ? { ...r, ...data } : r)));
      toast.success(`Billing updated for ${row.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSavingId(null); }
  };

  if (user && !user.platform_admin) return null;

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-6xl" data-testid="platform-admin-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-[#2563EB]" />
        <p className="overline">Platform administration</p>
      </div>
      <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">Workspace Billing</h1>
      <p className="text-sm text-slate-500 mt-1 max-w-2xl">
        Assign a plan to each Workspace and grant Complimentary / Internal access (full access at €0). Beta: no paywall is enforced — these settings prepare the model for a future payment provider.
      </p>

      <div className="mt-6 space-y-3">
        {rows.map((row) => (
          <Card key={row.company_id} className="p-5 border-slate-200" data-testid={`ws-billing-${row.company_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 truncate">{row.name}</h3>
                  <Badge className={`text-[10px] capitalize ${planColor(row.plan)}`}>{row.plan}</Badge>
                  {row.complimentary && <Badge className="text-[10px] bg-amber-100 text-amber-700"><Gift className="h-3 w-3 mr-1" />Complimentary · €0</Badge>}
                  {row.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                  {row.status === "inactive" && <Badge className="text-[10px] bg-red-100 text-red-700">Deactivated</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1 font-mono">{row.company_id} · {row.members} members · {row.projects} projects</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="text-[11px] text-slate-500">Plan</label>
                <Select value={row.plan} onValueChange={(v) => patch(row.company_id, "plan", v)}>
                  <SelectTrigger className="mt-1 h-9" data-testid={`plan-select-${row.company_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.plans.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Billing status</label>
                <Select value={row.billing_status} onValueChange={(v) => patch(row.company_id, "billing_status", v)}>
                  <SelectTrigger className="mt-1 h-9" data-testid={`status-select-${row.company_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.billing_statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Billing customer ID (future provider)</label>
                <Input className="mt-1 h-9 font-mono text-xs" placeholder="e.g. cus_… (optional)" value={row.billing_customer_id || ""} onChange={(e) => patch(row.company_id, "billing_customer_id", e.target.value)} data-testid={`customer-id-${row.company_id}`} />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Trial end date</label>
                <Input type="date" className="mt-1 h-9" value={(row.trial_end_date || "").slice(0, 10)} onChange={(e) => patch(row.company_id, "trial_end_date", e.target.value)} data-testid={`trial-end-${row.company_id}`} />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={!!row.complimentary} onCheckedChange={(v) => patch(row.company_id, "complimentary", v)} data-testid={`complimentary-switch-${row.company_id}`} />
                Complimentary / Internal (full access, €0)
              </label>
              <Button onClick={() => save(row)} disabled={savingId === row.company_id} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid={`save-billing-${row.company_id}`}>
                {savingId === row.company_id ? "Saving…" : "Save"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6">
        <LegalConfigCard />
      </div>
    </div>
  );
}
